import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Home, Building2, Store, Warehouse, MapPin, Bed, Bath, Maximize, ChevronRight, Menu, X, ArrowRight, MessageCircle, ExternalLink } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const HERO_VIDEOS = [
  '/api/media/79db120e-0f1',
  '/api/media/7420a3f2-481',
  '/api/media/79db120e-0f1',
];
const HERO_FALLBACK = 'https://customer-assets.emergentagent.com/job_98495428-5c53-48f5-8d26-c8cbe5905854/artifacts/iraxbf8q_New%20Building.jpg';

const NAV_KEYS = [
  { key: 'properties', href: '#properties' },
  { key: 'services', href: '/services' },
  { key: 'education', href: '/education' },
  { key: 'media', href: '/media' },
  { key: 'our_team', href: '/team' },
  { key: 'empire_group', href: '/empire-group' },
];

const LANG_OPTIONS = [
  { code: 'en', flag: 'EN' },
  { code: 'th', flag: 'TH' },
  { code: 'zh', flag: 'CN' },
  { code: 'ru', flag: 'RU' },
];

const STAT_KEYS = [
  { value: '500+', key: 'stat_properties' },
  { value: '7', key: 'stat_cities' },
  { value: '10+', key: 'stat_experience' },
  { value: '100%', key: 'stat_clients' },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const { language, changeLanguage, t } = useLanguage();
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [filters, setFilters] = useState({ propertyType: 'all', listingType: 'all', city: '' });
  const [currentVideo, setCurrentVideo] = useState(0);
  const videoRef = useRef(null);

  const handleVideoEnded = useCallback(() => {
    setCurrentVideo(prev => (prev + 1) % HERO_VIDEOS.length);
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    }
  }, [currentVideo]);

  useEffect(() => { fetchProperties(); }, []);
  useEffect(() => { applyFilters(); }, [filters, properties]);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data);
      setFilteredProperties(response.data);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const applyFilters = () => {
    let f = [...properties];
    if (filters.propertyType !== 'all') f = f.filter(p => p.property_type === filters.propertyType);
    if (filters.listingType !== 'all') f = f.filter(p => p.listing_type === filters.listingType);
    if (filters.city) f = f.filter(p => p.city?.toLowerCase().includes(filters.city.toLowerCase()));
    setFilteredProperties(f);
  };

  const getPropertyIcon = (type) => {
    const icons = { House: Home, Condo: Building2, Commercial: Warehouse, Retail: Store };
    const Icon = icons[type] || Home;
    return <Icon className="w-3.5 h-3.5" />;
  };

  const formatPrice = (price, listingType) => {
    const formatted = new Intl.NumberFormat('en-US').format(price);
    return listingType === 'Sale' ? formatted : `${formatted}/mo`;
  };

  const featuredProperties = properties.filter(p => p.featured || p.banner);
  const scrollToProperties = () => document.getElementById('properties')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-[#0A0E27]">
      {/* ===== HEADER ===== */}
      <header className="crystal-glass fixed top-0 left-0 right-0 z-50" data-testid="main-header">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex-shrink-0" data-testid="logo-link">
              <h1 className="font-heading text-xl md:text-2xl font-semibold text-[#D4AF37] tracking-tight">
                NOK <span className="text-white/40 font-light mx-1">|</span> <span className="text-white font-light text-base md:text-lg">Thailand Property</span>
              </h1>
            </Link>
            <nav className="hidden lg:flex items-center gap-10" data-testid="desktop-nav">
              {NAV_KEYS.map(link => (
                <Link key={link.key} to={link.href.startsWith('#') ? '/' : link.href}
                  onClick={link.href === '#properties' ? (e) => { e.preventDefault(); scrollToProperties(); } : undefined}
                  className="nav-link text-[#94A3B8] hover:text-white text-sm font-medium tracking-wide"
                  data-testid={`nav-${link.key}`}
                >{t(link.key)}</Link>
              ))}
            </nav>
            <div className="hidden lg:flex items-center gap-4">
              <div className="flex items-center gap-1 bg-white/5 rounded-full px-1 py-1" data-testid="language-selector">
                {LANG_OPTIONS.map(lang => (
                  <button key={lang.code} onClick={() => changeLanguage(lang.code)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors duration-300 ${language === lang.code ? 'bg-[#D4AF37] text-[#0A0E27]' : 'text-[#94A3B8] hover:text-white'}`}
                    data-testid={`lang-${lang.code}`}
                  >{lang.flag}</button>
                ))}
              </div>
              <Button onClick={() => navigate('/login')} variant="ghost" className="text-[#94A3B8] hover:text-white hover:bg-white/5 text-sm rounded-full px-5" data-testid="login-button">{t('sign_in')}</Button>
              <Button onClick={() => navigate('/register')} className="bg-[#D4AF37] hover:bg-[#E2C364] text-[#0A0E27] font-semibold text-sm rounded-full px-6" data-testid="register-button">{t('register')}</Button>
            </div>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden text-white p-2" data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="lg:hidden bg-[#131A38] border-t border-white/5 px-6 py-6 space-y-4" data-testid="mobile-menu">
            {NAV_KEYS.map(link => (
              <Link key={link.key} to={link.href.startsWith('#') ? '/' : link.href}
                onClick={() => { setMobileMenuOpen(false); if (link.href === '#properties') scrollToProperties(); }}
                className="block text-[#94A3B8] hover:text-white text-base font-medium py-2"
              >{t(link.key)}</Link>
            ))}
            <div className="flex gap-2 pt-4 border-t border-white/5">
              {LANG_OPTIONS.map(lang => (
                <button key={lang.code} onClick={() => changeLanguage(lang.code)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium ${language === lang.code ? 'bg-[#D4AF37] text-[#0A0E27]' : 'bg-white/5 text-[#94A3B8]'}`}
                >{lang.flag}</button>
              ))}
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={() => { navigate('/login'); setMobileMenuOpen(false); }} variant="ghost" className="text-[#94A3B8] hover:text-white text-sm flex-1 rounded-full">{t('sign_in')}</Button>
              <Button onClick={() => { navigate('/register'); setMobileMenuOpen(false); }} className="bg-[#D4AF37] hover:bg-[#E2C364] text-[#0A0E27] text-sm flex-1 rounded-full font-semibold">{t('register')}</Button>
            </div>
            <Button onClick={() => { navigate('/admin/login'); setMobileMenuOpen(false); }} variant="ghost" className="w-full text-[#94A3B8] hover:text-white text-xs mt-2 rounded-full" data-testid="admin-login-button-mobile">{t('admin_access')}</Button>
          </div>
        )}
      </header>

      {/* ===== HERO ===== */}
      <section className="relative h-screen flex items-end" data-testid="hero-section">
        <div className="absolute inset-0">
          <video
            ref={videoRef}
            key={currentVideo}
            muted
            playsInline
            onEnded={handleVideoEnded}
            className="w-full h-full object-cover"
            poster={HERO_FALLBACK}
            data-testid="hero-video"
          >
            <source src={HERO_VIDEOS[currentVideo]} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E27] via-[#0A0E27]/25 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0E27]/50 to-transparent" />
        </div>
        {/* Ghost watermark */}
        <div className="ghost-logo ghost-logo-hero" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pb-28 w-full">
          <div className="max-w-2xl">
            <span className="inline-block overline text-[#D4AF37] mb-6 fade-in-up" data-testid="hero-overline">{t('hero_overline')}</span>
            <h2 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-semibold text-white leading-[1.05] tracking-tight mb-6 fade-in-up delay-1" data-testid="hero-title">
              {t('hero_title_1')}<br />&amp; <span className="gradient-text">{t('hero_title_2')}</span>
            </h2>
            <p className="text-lg text-[#94A3B8] max-w-lg leading-relaxed mb-10 fade-in-up delay-2" data-testid="hero-subtitle">
              {t('hero_subtitle')}
            </p>
            <div className="flex flex-wrap gap-4 fade-in-up delay-3">
              <Button onClick={scrollToProperties} className="bg-[#D4AF37] hover:bg-[#E2C364] text-[#0A0E27] font-semibold rounded-full px-8 h-12 text-sm tracking-wide" data-testid="explore-properties-btn">
                {t('explore_properties')} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button onClick={() => navigate('/team')} variant="outline" className="border-white/20 text-white hover:bg-white/10 hover:border-white/30 rounded-full px-8 h-12 text-sm tracking-wide" data-testid="meet-team-btn">
                {t('meet_our_team')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== STATS ===== */}
      <section className="py-20 px-6 lg:px-8 border-b border-white/5" data-testid="stats-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-0 md:divide-x divide-white/10">
            {STAT_KEYS.map((stat, i) => (
              <div key={i} className="text-center px-4">
                <p className="font-heading text-4xl md:text-5xl font-semibold text-[#D4AF37] mb-2">{stat.value}</p>
                <p className="text-[#94A3B8] text-sm tracking-wide">{t(stat.key)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FEATURED PROPERTIES ===== */}
      {featuredProperties.length > 0 && (
        <section className="py-24 px-6 lg:px-8" data-testid="featured-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-14">
              <div>
                <span className="overline text-[#D4AF37] mb-3 block">{t('featured_overline')}</span>
                <h3 className="font-heading text-4xl md:text-5xl font-semibold text-white tracking-tight">{t('featured_title')}</h3>
              </div>
              <div className="hidden md:block gold-line w-48 mb-3" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProperties.slice(0, 6).map(property => (
                <PropertyCard key={property.id} property={property} navigate={navigate} formatPrice={formatPrice} getPropertyIcon={getPropertyIcon} featured />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== ABOUT / VALUE PROP ===== */}
      <section className="relative py-24 px-6 lg:px-8 bg-[#07091B] overflow-hidden" data-testid="about-section">
        <div className="ghost-logo ghost-logo-section" />
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="overline text-[#D4AF37] mb-4 block">{t('about_overline')}</span>
            <h3 className="font-heading text-4xl md:text-5xl font-semibold text-white tracking-tight mb-6 leading-tight">
              {t('about_title')}
            </h3>
            <p className="text-[#94A3B8] leading-relaxed mb-6">
              {t('about_desc')}
            </p>
            <div className="space-y-4 mb-8">
              {[t('about_item_1'), t('about_item_2'), t('about_item_3'), t('about_item_4')].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                    <ChevronRight className="w-3.5 h-3.5 text-[#D4AF37]" />
                  </div>
                  <span className="text-white text-sm">{item}</span>
                </div>
              ))}
            </div>
            <Button onClick={() => navigate('/services')} variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-full px-8 h-12 text-sm" data-testid="view-services-btn">
              {t('view_our_services')} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          <div className="relative">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-white/5">
              <img src="https://customer-assets.emergentagent.com/job_thailandrealty/artifacts/ejrcfdf8_S__7282706.jpg" alt="NOK Thailand Property" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#D4AF37] rounded-2xl p-6 max-w-[220px]">
              <p className="font-heading text-3xl font-bold text-[#0A0E27]">10+</p>
              <p className="text-[#0A0E27]/70 text-sm font-medium">Years of trusted service across Thailand</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== AREAS ===== */}
      <section className="py-24 px-6 lg:px-8" data-testid="areas-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <span className="overline text-[#D4AF37] mb-3 block">04 — Areas of Expertise</span>
            <h3 className="font-heading text-4xl md:text-5xl font-semibold text-white tracking-tight">Communities We Serve</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {['Pattaya', 'Bangkok', 'Phuket', 'Chiang Mai', 'Hua Hin', 'Chonburi', 'Rayong', 'Sattahip'].map(city => (
              <button key={city} onClick={() => { setFilters({ ...filters, city }); scrollToProperties(); }}
                className="bg-[#131A38] border border-white/5 hover:border-[#D4AF37]/30 rounded-xl p-5 text-left group transition-all duration-300"
                data-testid={`area-${city.toLowerCase()}`}
              >
                <MapPin className="w-5 h-5 text-[#D4AF37]/50 mb-3 group-hover:text-[#D4AF37] transition-colors duration-300" />
                <p className="text-white font-medium text-sm group-hover:text-[#D4AF37] transition-colors duration-300">{city}</p>
                <p className="text-[#475569] text-xs mt-1">View properties</p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FILTERS + PROPERTIES ===== */}
      <section id="properties" className="relative py-24 px-6 lg:px-8 scroll-mt-24 bg-[#07091B] overflow-hidden" data-testid="properties-section">
        <div className="ghost-logo ghost-logo-center" />
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-14">
            <div>
              <span className="overline text-[#D4AF37] mb-3 block">05 — Browse Collection</span>
              <h3 className="font-heading text-4xl md:text-5xl font-semibold text-white tracking-tight">All Properties</h3>
            </div>
            <p className="text-[#94A3B8] text-sm hidden md:block" data-testid="properties-count">
              {filteredProperties.length} {filteredProperties.length === 1 ? 'property' : 'properties'}
            </p>
          </div>

          {/* Filters */}
          <div className="bg-[#131A38] rounded-2xl p-6 mb-12 border border-white/5" data-testid="filters-panel">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div data-testid="filter-property-type">
                <label className="overline text-[#94A3B8] mb-2 block">Property Type</label>
                <Select value={filters.propertyType} onValueChange={v => setFilters({...filters, propertyType: v})}>
                  <SelectTrigger className="border-white/10 bg-[#0A0E27] text-white focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] rounded-xl h-11"><SelectValue placeholder="All Types" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="House">House</SelectItem>
                    <SelectItem value="Condo">Condo</SelectItem>
                    <SelectItem value="Land">Land</SelectItem>
                    <SelectItem value="Commercial">Commercial</SelectItem>
                    <SelectItem value="Retail">Retail</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div data-testid="filter-listing-type">
                <label className="overline text-[#94A3B8] mb-2 block">Listing Type</label>
                <Select value={filters.listingType} onValueChange={v => setFilters({...filters, listingType: v})}>
                  <SelectTrigger className="border-white/10 bg-[#0A0E27] text-white focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] rounded-xl h-11"><SelectValue placeholder="All Listings" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Listings</SelectItem>
                    <SelectItem value="Sale">For Sale</SelectItem>
                    <SelectItem value="Rental">For Rent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div data-testid="filter-city">
                <label className="overline text-[#94A3B8] mb-2 block">Location</label>
                <Select value={filters.city || 'all'} onValueChange={v => setFilters({...filters, city: v === 'all' ? '' : v})}>
                  <SelectTrigger className="border-white/10 bg-[#0A0E27] text-white focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] rounded-xl h-11"><SelectValue placeholder="All Locations" /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectItem value="all">All Locations</SelectItem>
                    {['Bangkok', 'Pattaya', 'Phuket', 'Chiang Mai', 'Hua Hin', 'Koh Samui', 'Chon Buri', 'Rayong'].map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button onClick={() => setFilters({ propertyType: 'all', listingType: 'all', city: '' })} variant="outline"
                  className="w-full border-white/10 text-[#94A3B8] hover:bg-white/5 hover:text-white hover:border-white/20 rounded-xl h-11 text-sm" data-testid="clear-filters-button">
                  Clear Filters
                </Button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-32">
              <div className="inline-block h-10 w-10 animate-spin rounded-full border-2 border-solid border-[#D4AF37] border-r-transparent" />
            </div>
          ) : filteredProperties.length === 0 ? (
            <div className="text-center py-32 bg-[#131A38] rounded-2xl border border-white/5" data-testid="no-properties-message">
              <Building2 className="w-12 h-12 mx-auto text-[#D4AF37]/40 mb-4" />
              <p className="text-[#94A3B8]">No properties found matching your criteria</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="properties-grid">
              {filteredProperties.map(property => (
                <PropertyCard key={property.id} property={property} navigate={navigate} formatPrice={formatPrice} getPropertyIcon={getPropertyIcon} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="border-t border-white/5 bg-[#07091B]" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
            <div className="md:col-span-1">
              <h4 className="font-heading text-2xl font-semibold text-[#D4AF37] tracking-tight mb-4">NOK</h4>
              <p className="text-[#94A3B8] text-sm leading-relaxed">
                {t('footer_desc')}
              </p>
            </div>
            <div>
              <p className="overline text-white mb-5">{t('quick_links')}</p>
              <ul className="space-y-3">
                {[['properties', '/'], ['services', '/services'], ['education', '/education'], ['media', '/media'], ['our_team', '/team']].map(([key, href]) => (
                  <li key={key}><Link to={href} className="text-[#94A3B8] hover:text-[#D4AF37] text-sm transition-colors duration-300">{t(key)}</Link></li>
                ))}
              </ul>
            </div>
            <div>
              <p className="overline text-white mb-5">{t('locations')}</p>
              <ul className="space-y-3">
                {['Pattaya', 'Bangkok', 'Phuket', 'Chiang Mai', 'Hua Hin', 'Chonburi'].map(city => (
                  <li key={city}><span className="text-[#94A3B8] text-sm">{city}</span></li>
                ))}
              </ul>
            </div>
            <div>
              <p className="overline text-white mb-5">{t('contact')}</p>
              <ul className="space-y-3">
                <li className="text-[#94A3B8] text-sm">Pattaya, Chonburi, Thailand</li>
                <li>
                  <a href="https://lin.ee/soB6jcI" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#06C755] hover:text-[#05b34d] text-sm font-medium transition-colors duration-300" data-testid="footer-line-realestate">
                    <MessageCircle className="w-3.5 h-3.5" /> LINE — Real Estate <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
                <li>
                  <a href="https://lin.ee/soB6jcI" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#06C755] hover:text-[#05b34d] text-sm font-medium transition-colors duration-300" data-testid="footer-line-legal">
                    <MessageCircle className="w-3.5 h-3.5" /> LINE — Legal <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
                <li>
                  <a href="https://page.line.me/296nejkj" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#06C755] hover:text-[#05b34d] text-sm font-medium transition-colors duration-300" data-testid="footer-line-pook">
                    <MessageCircle className="w-3.5 h-3.5" /> {t('line_pook')} <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
                <li><Link to="/register" className="text-[#D4AF37] hover:text-[#E2C364] text-sm transition-colors duration-300">{t('register_for_updates')}</Link></li>
                <li><Link to="/admin/login" className="text-[#94A3B8] hover:text-white text-sm transition-colors duration-300" data-testid="admin-login-button">{t('admin_access')}</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-[#475569] text-xs">&copy; {new Date().getFullYear()} NOK Thailand Property &amp; Investment. All rights reserved.</p>
            <div className="flex gap-6">
              <Link to="/empire-group" className="text-[#475569] hover:text-[#94A3B8] text-xs transition-colors duration-300">Empire Group</Link>
              <span className="text-[#475569] text-xs">Real Estate &amp; Hospitality</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

const PropertyCard = ({ property, navigate, formatPrice, getPropertyIcon, featured }) => (
  <div className={`property-card bg-[#131A38] rounded-2xl overflow-hidden border cursor-pointer group ${featured ? 'border-[#D4AF37]/20 hover:border-[#D4AF37]/50' : 'border-white/5 hover:border-[#D4AF37]/30'}`}
    onClick={() => navigate(`/property/${property.id}`)} data-testid={`property-card-${property.id}`}
  >
    <div className="relative h-64 overflow-hidden bg-[#0A0E27]">
      {property.images?.length > 0 ? (
        <img src={property.images[0]} alt={property.title} className="property-image w-full h-full object-cover" />
      ) : (
        <div className="flex items-center justify-center h-full"><Building2 className="w-12 h-12 text-[#D4AF37]/20" /></div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-[#131A38] via-transparent to-transparent opacity-60" />
      <div className="absolute top-4 left-4 flex gap-2">
        <span className="px-3 py-1 bg-[#D4AF37] text-[#0A0E27] text-[10px] font-semibold rounded-full uppercase tracking-wider flex items-center gap-1.5">
          {getPropertyIcon(property.property_type)} {property.property_type}
        </span>
        <span className="px-3 py-1 bg-white/10 backdrop-blur-md text-white text-[10px] font-semibold rounded-full uppercase tracking-wider">{property.listing_type}</span>
      </div>
      {property.banner && (
        <div className="absolute top-4 right-4">
          <span className={`px-3 py-1 text-[10px] font-bold rounded-full uppercase tracking-wider ${
            property.banner === 'HOT LISTING' ? 'bg-red-500/90 text-white' :
            property.banner === 'PRICE DROP' ? 'bg-emerald-500/90 text-white' :
            'bg-orange-500/90 text-white'
          }`}>{property.banner}</span>
        </div>
      )}
    </div>
    <div className="p-6">
      <h4 className="font-heading text-xl font-semibold text-white mb-2 line-clamp-1 group-hover:text-[#D4AF37] transition-colors duration-300" data-testid={`property-title-${property.id}`}>{property.title}</h4>
      <div className="flex items-center text-[#94A3B8] mb-4">
        <MapPin className="w-3.5 h-3.5 mr-1.5 text-[#D4AF37]/60" />
        <span className="text-sm">{property.city}{property.location && property.location !== property.city ? `, ${property.location}` : ''}</span>
      </div>
      {(property.bedrooms || property.bathrooms || property.size_sqm) && (
        <div className="flex items-center gap-4 text-xs text-[#94A3B8] mb-5 pb-5 border-b border-white/5">
          {property.bedrooms && <div className="flex items-center gap-1.5"><Bed className="w-3.5 h-3.5 text-[#D4AF37]/50" /><span>{property.bedrooms} Bed</span></div>}
          {property.bathrooms && <div className="flex items-center gap-1.5"><Bath className="w-3.5 h-3.5 text-[#D4AF37]/50" /><span>{property.bathrooms} Bath</span></div>}
          {property.size_sqm && <div className="flex items-center gap-1.5"><Maximize className="w-3.5 h-3.5 text-[#D4AF37]/50" /><span>{property.size_sqm} m&sup2;</span></div>}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <p className="overline text-[#94A3B8] mb-1">Price</p>
          <p className="text-xl font-semibold text-[#D4AF37]" data-testid={`property-price-${property.id}`}>&#3647;{formatPrice(property.price, property.listing_type)}</p>
        </div>
        <button className="flex items-center gap-1.5 text-[#94A3B8] group-hover:text-[#D4AF37] text-sm font-medium transition-colors duration-300" data-testid={`view-details-button-${property.id}`}>
          View <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  </div>
);

export default LandingPage;
